import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';

/**
 * Data access for the audit log viewer.
 *
 * `AuditLogsController` was already complete, so nothing here changes the
 * backend — which is why this screen does *not* use `useServerDataGrid`. That
 * hook posts `{ page, pageSize, sortBy, sortDir, search, filters }`; this
 * controller takes `AuditLogFilterDto`, with its filters as first-class fields
 * and `PageNumber`/`SortDirection` spellings. Rewriting the contract to fit the
 * hook would break the Razor page still using it, for no gain before Phase 5, so
 * the paging state is managed here instead and fed to `<DataGrid>` by hand.
 */

const BASE = '/api/AuditLogs';

/** Mirrors `EntryState`; the API takes and returns the numeric value. */
export const OperationType = {
  Added: 1,
  Deleted: 2,
  Modified: 3,
  Logout: 4,
  Login: 5,
} as const;

export const operationTypeNames: Record<number, string> = {
  1: 'Added',
  2: 'Deleted',
  3: 'Modified',
  4: 'Logout',
  5: 'Login',
};

/** Badge tone per operation, so a deletion reads differently from a login. */
export const operationTypeTone: Record<number, string> = {
  1: 'bg-success',
  2: 'bg-danger',
  3: 'bg-warning',
  4: 'bg-secondary',
  5: 'bg-info',
};

export interface AuditLogFilter {
  dateFrom?: string | null;
  dateTo?: string | null;
  operationType?: number | null;
  objectType?: string | null;
  owner?: string | null;
  ipAddress?: string | null;
  isSuccess?: boolean | null;
  searchTerm?: string | null;
  pageNumber: number;
  pageSize: number;
  sortBy: string;
  sortDirection: 'asc' | 'desc';
}

export interface AuditLogListItem {
  id: number;
  operationDate: string;
  owner: string;
  objectType: string;
  objectId: string | null;
  operationType: number;
  action: string | null;
  ipAddress: string;
  isSuccess: boolean;
  errorMessage: string | null;
  detailPreview: string | null;
}

export interface AuditLogDetail extends AuditLogListItem {
  userId: string;
  detail: string | null;
  userAgent: string | null;
  metadata: string | null;
}

export interface AuditLogPage {
  items: AuditLogListItem[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export const auditLogKeys = {
  all: ['admin', 'audit-logs'] as const,
  search: (filter: AuditLogFilter) => ['admin', 'audit-logs', 'search', filter] as const,
  detail: (id: number) => ['admin', 'audit-logs', 'detail', id] as const,
  objectTypes: ['admin', 'audit-logs', 'object-types'] as const,
  owners: ['admin', 'audit-logs', 'owners'] as const,
};

/**
 * One page of log entries.
 *
 * `placeholderData` keeps the previous page on screen while the next loads, so
 * paging does not blink through an empty table.
 */
export function useAuditLogSearch(filter: AuditLogFilter) {
  return useQuery<AuditLogPage>({
    queryKey: auditLogKeys.search(filter),
    queryFn: () => api.post<AuditLogPage>(`${BASE}/search`, filter),
    placeholderData: keepPreviousData,
  });
}

/** One entry in full, fetched only when a row is opened. */
export function useAuditLogDetail(id: number | null) {
  return useQuery<AuditLogDetail>({
    queryKey: auditLogKeys.detail(id ?? -1),
    queryFn: () => api.get<AuditLogDetail>(`${BASE}/${id}`),
    enabled: id != null,
  });
}

/** Distinct values behind the Object type and Owner dropdowns. */
export function useAuditLogObjectTypes() {
  return useQuery<string[]>({
    queryKey: auditLogKeys.objectTypes,
    queryFn: () => api.get<string[]>(`${BASE}/object-types`),
    staleTime: 5 * 60 * 1000,
  });
}

export function useAuditLogOwners() {
  return useQuery<string[]>({
    queryKey: auditLogKeys.owners,
    queryFn: () => api.get<string[]>(`${BASE}/owners`),
    staleTime: 5 * 60 * 1000,
  });
}

function useInvalidateAuditLogs() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: auditLogKeys.all });
}

/** Deletes the selected entries. Returns how many rows went. */
export function useDeleteAuditLogs() {
  const invalidate = useInvalidateAuditLogs();
  return useMutation({
    mutationFn: (ids: number[]) => api.delete<number>(BASE, { body: { ids } }),
    onSuccess: invalidate,
  });
}

/** Deletes everything older than a date. Returns how many rows went. */
export function useDeleteOldAuditLogs() {
  const invalidate = useInvalidateAuditLogs();
  return useMutation({
    mutationFn: (olderThan: string) =>
      api.delete<number>(`${BASE}/older-than`, { body: { olderThan } }),
    onSuccess: invalidate,
  });
}

/**
 * Downloads the current filter's entries as CSV.
 *
 * A POST, not a link: the endpoint takes the same `AuditLogFilterDto` the grid
 * queries with, so the file matches what is on screen. That rules out the
 * anchor-href approach the XLSX exports use and means the blob is fetched here.
 */
export async function downloadAuditLogCsv(filter: AuditLogFilter): Promise<void> {
  const response = await fetch(`${BASE}/export`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      ...xsrfHeader(),
    },
    body: JSON.stringify(filter),
  });

  if (!response.ok || !(response.headers.get('Content-Type') ?? '').includes('csv')) {
    throw new Error(`Export failed with status ${response.status}`);
  }

  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);

  const blobUrl = window.URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = match ? decodeURIComponent(match[1]) : 'AuditLogs.csv';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
}

/** The antiforgery header the API client sends on every mutating request. */
function xsrfHeader(): Record<string, string> {
  const match = document.cookie.match(/(?:^|; )XSRF-TOKEN=([^;]*)/);
  return match ? { RequestVerificationToken: decodeURIComponent(match[1]) } : {};
}
